import type {
  AbilityScores,
  Character,
  RaceId,
  ClassId,
  BackgroundId,
  SkillName,
  AbilityName,
  FormationSlot,
} from "../types/entity.js";
import { DEATH_SAVE_NONE } from "../types/entity.js";
import {
  applyRacialBonuses,
  createDefaultAllocation,
  validateAllocation,
} from "./pointBuy.js";

/**
 * Character-creator wizard state machine (C10).
 *
 * Splits the UI-heavy wizard from its underlying state so the state
 * transitions can be unit-tested without React. Each step advances the
 * `CharacterDraft` by a single field; `finalizeCharacter` applies racial
 * bonuses and assembles the full `Character` record once every step is
 * complete.
 *
 * This deliberately does *not* talk to `DataRegistry` — race/class/
 * background lookups happen in the UI layer (or tests), so the state
 * machine stays pure.
 */

export type WizardStep = "race" | "class" | "abilities" | "background" | "name" | "review";

export const WIZARD_STEP_ORDER: readonly WizardStep[] = [
  "race",
  "class",
  "abilities",
  "background",
  "name",
  "review",
];

export interface CharacterDraft {
  readonly race: RaceId | null;
  readonly characterClass: ClassId | null;
  readonly abilities: AbilityScores;
  readonly background: BackgroundId | null;
  readonly name: string;
  readonly step: WizardStep;
}

/** Fresh draft — starts on the race step with a default 8/8/8/8/8/8 allocation. */
export function createDraft(): CharacterDraft {
  return {
    race: null,
    characterClass: null,
    abilities: createDefaultAllocation(),
    background: null,
    name: "",
    step: "race",
  };
}

// ── Step navigation ──────────────────────────────────────────────────────────

/** True if the wizard can advance *past* the current step. */
export function isStepComplete(draft: CharacterDraft): boolean {
  switch (draft.step) {
    case "race":
      return draft.race !== null;
    case "class":
      return draft.characterClass !== null;
    case "abilities":
      return validateAllocation(draft.abilities).valid;
    case "background":
      return draft.background !== null;
    case "name":
      return draft.name.trim().length > 0;
    case "review":
      return true;
  }
}

/** Advance to the next step if the current one is complete. */
export function nextStep(draft: CharacterDraft): CharacterDraft {
  if (!isStepComplete(draft)) return draft;
  const idx = WIZARD_STEP_ORDER.indexOf(draft.step);
  if (idx === -1 || idx === WIZARD_STEP_ORDER.length - 1) return draft;
  return { ...draft, step: WIZARD_STEP_ORDER[idx + 1]! };
}

/** Back up to the previous step (no-op if already on the first step). */
export function prevStep(draft: CharacterDraft): CharacterDraft {
  const idx = WIZARD_STEP_ORDER.indexOf(draft.step);
  if (idx <= 0) return draft;
  return { ...draft, step: WIZARD_STEP_ORDER[idx - 1]! };
}

// ── Per-step mutations ───────────────────────────────────────────────────────

export function setRace(draft: CharacterDraft, race: RaceId): CharacterDraft {
  return { ...draft, race };
}

export function setClass(draft: CharacterDraft, characterClass: ClassId): CharacterDraft {
  return { ...draft, characterClass };
}

export function setAbilities(draft: CharacterDraft, abilities: AbilityScores): CharacterDraft {
  return { ...draft, abilities };
}

export function setBackground(draft: CharacterDraft, background: BackgroundId): CharacterDraft {
  return { ...draft, background };
}

export function setName(draft: CharacterDraft, name: string): CharacterDraft {
  return { ...draft, name };
}

// ── Finalize ─────────────────────────────────────────────────────────────────

export interface FinalizeInput {
  readonly draft: CharacterDraft;
  readonly instanceId: string;
  /** Racial ability bonuses (from the race data record). */
  readonly racialBonuses?: Partial<AbilityScores>;
  /** Skill proficiencies granted by class + background. */
  readonly skillProficiencies?: readonly SkillName[];
  /** Save proficiencies from class data. */
  readonly saveProficiencies?: readonly AbilityName[];
  /** Starting HP from class data. */
  readonly maxHp: number;
  /** Starting AC (base 10 + DEX mod unless armored). */
  readonly armorClass: number;
  /** Initial spell slots from class table. */
  readonly spellSlots?: Record<number, number>;
  /** EV-E7 — casting stat for roster-provided custom classes. */
  readonly castingAbility?: AbilityName;
  /** Optional portrait data-URL (L04 output, or fallback SVG). */
  readonly portrait?: string | null;
  /** Optional formation slot; null if not yet assigned. */
  readonly formationSlot?: FormationSlot | null;
}

export class IncompleteDraftError extends Error {
  constructor(readonly missing: readonly string[]) {
    super(`Character draft is missing: ${missing.join(", ")}`);
    this.name = "IncompleteDraftError";
  }
}

/**
 * Assemble a complete `Character` from the wizard draft. Throws
 * `IncompleteDraftError` listing every missing field when the draft
 * hasn't passed every wizard step.
 */
export function finalizeCharacter(input: FinalizeInput): Character {
  const { draft } = input;
  const missing: string[] = [];
  if (!draft.race) missing.push("race");
  if (!draft.characterClass) missing.push("class");
  if (!validateAllocation(draft.abilities).valid) missing.push("abilities");
  if (!draft.background) missing.push("background");
  if (!draft.name.trim()) missing.push("name");
  if (missing.length) throw new IncompleteDraftError(missing);

  const abilities = applyRacialBonuses(draft.abilities, input.racialBonuses ?? {});

  return {
    kind: "player",
    instanceId: input.instanceId,
    dataId: draft.characterClass!,
    name: draft.name.trim(),
    characterClass: draft.characterClass!,
    race: draft.race!,
    background: draft.background!,
    portrait: input.portrait ?? null,
    level: 1,
    position: { x: 0, z: 0 },
    hp: input.maxHp,
    maxHp: input.maxHp,
    armorClass: input.armorClass,
    speed: { walk: 30 },
    abilities,
    proficiencyBonus: 2,
    spellSlots: input.spellSlots ?? {},
    ...(input.castingAbility !== undefined ? { castingAbility: input.castingAbility } : {}),
    xp: 0,
    knownSpells: [],
    reactions: [],
    skillProficiencies: new Set(input.skillProficiencies ?? []),
    saveProficiencies: new Set(input.saveProficiencies ?? []),
    expertiseSkills: new Set(),
    conditions: new Set(),
    deathSaves: DEATH_SAVE_NONE,
    formationSlot: input.formationSlot ?? null,
  };
}
